import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Button, CircularProgress } from '@mui/material';
import { Feedback } from '@fashionnovateam/nova-ui-toolkit';
import { omit } from 'lodash';
import styles from './DimensionsModal.module.css';
import { Tabs } from '../../ReusableComponents/Tabs/Tabs';
import Dot from '../../ReusableComponents/Dot/Dot';
import { countMissingDetails } from '../utils/countMissingDetails';
import { catalogItemShape } from '../_common_/propTypes';
import SizeSelectInput from './SizeSelectInput';
import WeightInput from './WeightInputs';
import extractStyleNumber from '../utils/extractStyleNumber';
import {
  useMutateItems,
  useGetUserCatalogItems,
} from '../../../Lib/Hooks/catalog-item.hooks';
import toast from '../../Toast/Toast';

export default function DimensionsModal({
  defaultSelectedItem,
  selectedItemSkus = undefined,
  onSave,
  onClose,
}) {
  const currentUser = useLocation().pathname.split('/')[2];
  const [detailsStates, setDetailsStates] = useState([]);
  const [itemDimUpdates, setItemDimUpdates] = useState([]);
  const [selectedCatalogItem, setSelectedCatalogItem] =
    useState(defaultSelectedItem);
  const updateItemMutation = useMutateItems();

  const styleNumber = extractStyleNumber(defaultSelectedItem.sku);

  const { isLoading, isError, data } = useGetUserCatalogItems(currentUser, {
    skus: selectedItemSkus,
    styleNumber,
    similarItemsOnly: true,
    page: 1,
    offset: 0,
    limit: 50,
  });

  useEffect(() => {
    if (data) {
      const cleanedItems = data.items.map((item) => omit(item, ['division']));
      setDetailsStates(cleanedItems);
    }
  }, [data]);

  useEffect(() => {
    if (isError) {
      toast({ title: 'Error fetching catalog items' }, { type: 'error' });
      onClose();
    }
  }, [isError]);

  const resetUpdatedValues = () => {
    const newDetailsStates = [...detailsStates];
    newDetailsStates.forEach((item) => {
      // eslint-disable-next-line no-param-reassign
      item.updated = false;
    });
    setDetailsStates(newDetailsStates);
    setItemDimUpdates([]);
  };

  const saveItem = () => {
    if (!itemDimUpdates.length) return;

    const successCb = () => {
      onSave();
      resetUpdatedValues();
    };

    const errorCb = () => {};

    const mutationArgs = {
      currentUser,
      params: itemDimUpdates,
      successCb,
      errorCb,
    };

    updateItemMutation.mutate(mutationArgs);
  };

  const buildTitle = (title, isMissing) => (
    <>
      {isMissing ? <Dot color="red" /> : <Dot color="green" />}
      {title}
    </>
  );

  const handleTabChange = (index) => {
    if (!data) return;
    setSelectedCatalogItem(data.items[index]);
  };

  const handleDetailChange = (id, name, value) => {
    const newDetailsStates = [...detailsStates];
    const itemIdx = newDetailsStates.findIndex((item) => item.id === id);
    newDetailsStates[itemIdx][name] = value;
    newDetailsStates[itemIdx].updated = true;

    if (name === 'weight' && value !== null && value <= 0) {
      newDetailsStates[itemIdx].error = 'The weight must be greater than 0';
    } else {
      newDetailsStates[itemIdx].error = null;
    }

    const itemDimUpdatesIdx = itemDimUpdates.findIndex(
      (item) => item.id === id,
    );

    const cleanedItem = omit(
      {
        ...newDetailsStates[itemIdx],
      },
      ['division'],
    );

    if (itemDimUpdatesIdx !== -1) {
      itemDimUpdates[itemDimUpdatesIdx] = {
        ...itemDimUpdates[itemDimUpdatesIdx],
        ...cleanedItem,
      };
    } else {
      itemDimUpdates.push(cleanedItem);
    }
    setItemDimUpdates(itemDimUpdates);
    setDetailsStates(newDetailsStates);
  };

  const findStateById = (id) => {
    const itemState = detailsStates.find((item) => item.id === id);
    if (!itemState) return null;
    return itemState;
  };

  const updatedValues = detailsStates.filter((item) => item.updated);

  const areUpdatedValuesValid = () => {
    if (updatedValues.length === 0) return false;
    if (!updatedValues.every((item) => item.error === null)) return false;
    return true;
  };

  if (isLoading) {
    return (
      <div className={styles.contentContainer}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <form className={styles.contentContainer}>
      <div className={styles.contentSubtitleContainer}>
        <span className={styles.styleNumberText}>
          {selectedCatalogItem.vendor_style_number}
        </span>
      </div>
      <div className={styles.subtitleContainer}>
        <span>{selectedCatalogItem.wms_id}</span>
      </div>

      <Tabs
        onTabChange={handleTabChange}
        selectedIndex={detailsStates.findIndex(
          (item) => item.id === selectedCatalogItem.id,
        )}
      >
        {detailsStates.map((item) => {
          const itemState = findStateById(item.id);
          return (
            <div
              key={item.id}
              title={buildTitle(item.size, countMissingDetails(item) > 0)}
            >
              <Feedback
                title={
                  <>
                    Please enter only the weight and size of the{' '}
                    <span className="font-black">individual item.</span>
                  </>
                }
                description="This is not the carton or package weight"
                type="info"
              />
              <div className={styles.detailsContainer}>
                <span className={styles.detailsTitle}>Size of item</span>
                <span className={styles.detailsSubtitle}>Add in inches.</span>
                <div className={styles.sizeContainer}>
                  <SizeSelectInput
                    name="width"
                    currentValue={itemState.width}
                    onChange={(value) =>
                      handleDetailChange(item.id, 'width', value)
                    }
                  />
                  <SizeSelectInput
                    name="length"
                    currentValue={itemState.length}
                    onChange={(value) =>
                      handleDetailChange(item.id, 'length', value)
                    }
                  />
                  <SizeSelectInput
                    name="height"
                    currentValue={itemState.height}
                    onChange={(value) =>
                      handleDetailChange(item.id, 'height', value)
                    }
                  />
                </div>
              </div>
              <div className={styles.detailsContainer}>
                <span className={styles.detailsTitle}>Weight of item</span>
                <span className={styles.detailsSubtitle}>
                  Add in as ounces (oz) or pounds (lbs).
                </span>
                <div className={styles.sizeContainer}>
                  <WeightInput
                    unit="oz"
                    currentValue={
                      itemState.weight !== null ? itemState.weight * 16 : null
                    }
                    onChange={(value) => {
                      handleDetailChange(
                        item.id,
                        'weight',
                        value ? value / 16 : null,
                      );
                    }}
                  />
                  <span className={styles.weightEqual}>=</span>
                  <WeightInput
                    unit="lbs"
                    currentValue={itemState.weight}
                    onChange={(value) =>
                      handleDetailChange(item.id, 'weight', value)
                    }
                  />
                </div>
              </div>
              <div className={styles.footerContainer}>
                <div className={styles.buttonContainer}>
                  <Button
                    type="button"
                    variant="outlined"
                    size="large"
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    className={styles.submitButton}
                    type="button"
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={areUpdatedValuesValid() === false}
                    onClick={saveItem}
                  >
                    Update
                  </Button>
                </div>
                {updatedValues.map((udpatedItem) => {
                  if (udpatedItem.error)
                    return (
                      <span style={{ color: 'red' }} key={udpatedItem.id}>
                        {`Error on size ${udpatedItem.size}: ${udpatedItem.error}`}
                      </span>
                    );
                  return null;
                })}
              </div>
            </div>
          );
        })}
      </Tabs>
    </form>
  );
}

DimensionsModal.propTypes = {
  defaultSelectedItem: catalogItemShape.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
